/**
 * Asks which saved short to drop when the favorites list is full.
 *
 * The service refuses a favorite past the cap and answers limitReached with the
 * list it kept, so the only way forward is to free a slot. This renders that
 * list and resolves the id the customer picked, or null if they backed out. The
 * caller owns the delete and the retry, so this stays pure UI.
 *
 *   var id = await window.tigShortFavoriteLimitDialog(store.ids());
 *
 * Built on the native <dialog>: focus trapping, Escape, page inertness and the
 * backdrop come from the browser rather than being reimplemented. Where <dialog>
 * is unsupported it resolves null, leaving the customer where they started
 * rather than stranding them in a dialog they cannot dismiss.
 */
(function () {
  if (window.tigShortFavoriteLimitDialog) return;

  var DIALOG_ID = 'tig-short-favorite-limit-dialog';
  // Bumped per open, so a slow content request from a previous open cannot
  // rebuild the list under the current one.
  var openToken = 0;
  var TITLE = 'お気に入りが上限です';
  var FALLBACK_TITLE = 'ショート動画';
  var UNAVAILABLE_TITLE = 'この動画は公開を終了しました';

  function buildDialog() {
    var dialog = document.createElement('dialog');
    dialog.id = DIALOG_ID;
    dialog.className = 'tig-favorite-limit';
    dialog.innerHTML =
      '<div class="tig-favorite-limit__panel">' +
      '<p class="tig-favorite-limit__title"></p>' +
      '<p class="tig-favorite-limit__message"></p>' +
      '<ul class="tig-favorite-limit__list"></ul>' +
      '<div class="tig-favorite-limit__actions">' +
      '<button type="button" class="tig-favorite-limit__cancel">キャンセル</button>' +
      '<button type="button" class="tig-favorite-limit__ok" disabled>削除して追加</button>' +
      '</div>' +
      '</div>';
    document.body.appendChild(dialog);
    return dialog;
  }

  /**
   * One row per saved short. Radio, because freeing one slot is all it takes.
   * While pending, a missing content is not yet known to be gone, so the row
   * shows the neutral placeholder rather than accusing it of being unpublished.
   */
  function buildRow(id, content, index, pending) {
    var item = document.createElement('li');
    item.className = 'tig-favorite-limit__item';

    var unavailable = !content && !pending;
    var title = unavailable ? UNAVAILABLE_TITLE : (content && content.title) || FALLBACK_TITLE;
    var inputId = DIALOG_ID + '-choice-' + index;
    var poster = unavailable ? '' : window.TigShortFavoriteStore.posterUrl(content);

    item.innerHTML =
      '<input type="radio" name="tig-favorite-limit-choice" class="tig-favorite-limit__radio" ' +
      'id="' + inputId + '" value="' + id + '">' +
      '<label class="tig-favorite-limit__label" for="' + inputId + '">' +
      '<span class="tig-favorite-limit__thumb"></span>' +
      '<span class="tig-favorite-limit__name"></span>' +
      '</label>';

    // textContent rather than interpolation: titles are merchandiser-entered.
    item.querySelector('.tig-favorite-limit__name').textContent = title;
    if (poster) {
      var image = document.createElement('img');
      image.alt = '';
      image.loading = 'lazy';
      image.src = poster;
      item.querySelector('.tig-favorite-limit__thumb').appendChild(image);
    }
    if (unavailable) item.classList.add('is-unavailable');
    return item;
  }

  window.tigShortFavoriteLimitDialog = function (ids) {
    ids = (ids || []).map(String);
    var store = window.TigShortFavoriteStore;

    if (typeof HTMLDialogElement === 'undefined' || !store || ids.length === 0) {
      return Promise.resolve(null);
    }

    var token = ++openToken;
    var dialog = document.getElementById(DIALOG_ID) || buildDialog();
    var list = dialog.querySelector('.tig-favorite-limit__list');
    var okButton = dialog.querySelector('.tig-favorite-limit__ok');
    var cancelButton = dialog.querySelector('.tig-favorite-limit__cancel');

    dialog.querySelector('.tig-favorite-limit__title').textContent = TITLE;
    dialog.querySelector('.tig-favorite-limit__message').textContent =
      'お気に入りは' + store.MAX + '件までです。削除する動画を選択してください。';

    // Rows go up straight away, titles and posters fill in when the batch
    // request lands. Waiting on it first would leave the dialog blank.
    list.innerHTML = '';
    ids.forEach(function (id, index) {
      list.appendChild(buildRow(id, null, index, true));
    });
    list.classList.add('is-loading');

    store.fetchList(ids).then(function (resolved) {
      if (token !== openToken) return;
      var byId = {};
      resolved.contents.forEach(function (content) {
        byId[content.identifier] = content;
      });
      list.innerHTML = '';
      ids.forEach(function (id, index) {
        list.appendChild(buildRow(id, byId[id], index, false));
      });
      list.classList.remove('is-loading');
      // The rebuild drops any radio ticked while the titles were loading, so
      // the confirm button has to go back to matching an empty selection.
      okButton.disabled = true;
    });

    return new Promise(function (resolve) {
      function selected() {
        var checked = list.querySelector('.tig-favorite-limit__radio:checked');
        return checked ? checked.value : null;
      }

      function onChange() {
        okButton.disabled = !selected();
      }
      function onOk() {
        cleanup(selected());
      }
      function onCancel() {
        cleanup(null);
      }
      // Fires when the browser closes the dialog itself, i.e. Escape.
      function onClose() {
        cleanup(null);
      }
      function onBackdrop(event) {
        // The dialog fills the viewport and the panel is its only child, so a
        // click landing on the dialog itself is a backdrop click.
        if (event.target === dialog) cleanup(null);
      }

      function cleanup(choice) {
        list.removeEventListener('change', onChange);
        okButton.removeEventListener('click', onOk);
        cancelButton.removeEventListener('click', onCancel);
        dialog.removeEventListener('close', onClose);
        dialog.removeEventListener('click', onBackdrop);
        if (dialog.open) dialog.close();
        document.body.classList.remove('overflow-hidden');
        resolve(choice);
      }

      list.addEventListener('change', onChange);
      okButton.addEventListener('click', onOk);
      cancelButton.addEventListener('click', onCancel);
      dialog.addEventListener('close', onClose);
      dialog.addEventListener('click', onBackdrop);

      okButton.disabled = true;
      document.body.classList.add('overflow-hidden');
      dialog.showModal();
      // Default to the non-destructive action.
      cancelButton.focus();
    });
  };
})();
